import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { getCookie } from "./auth/cookies";
import { toast } from "react-toastify";
import { 
  FaTimes, 
  FaUserFriends, 
  FaInfoCircle, 
  FaUserCircle,
  FaMedal,
  FaSpinner
} from "react-icons/fa";

function TeamInfoModal({ onClose, team_id, user_id }) {
    const [teamInfo, setTeamInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const modalRef = useRef(null);

    // Fetch team information from API
    useEffect(() => {
        const fetchTeamInfo = async () => {
            try {
                setLoading(true);
                setError(null);
                
                const requestData = team_id ? { team_id } : { user_id }; // Pass either team_id or user_id
                const response = await axios.post(
                    '/api/other_team_info/', 
                    requestData, 
                    { headers: { 'X-CSRFToken': getCookie('csrftoken') } }
                );
                
                setTeamInfo(response.data.team);
            } catch (err) {
                console.error("Error fetching team info:", err);
                setError(err.response?.data?.error || err.message || "Failed to load team information");
                toast.error(`Error fetching team info: ${err.message}`);
            } finally {
                setLoading(false);
            }
        };
        
        fetchTeamInfo();
    }, [team_id, user_id]);

    // Handle click outside to close modal
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                onClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [onClose]);

    // Handle ESC key to close modal
    useEffect(() => {
        const handleEscKey = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        window.addEventListener('keydown', handleEscKey);
        return () => {
            window.removeEventListener('keydown', handleEscKey);
        };
    }, [onClose]);

    // Format team members list
    const formatMembers = (membersStr) => {
        if (!membersStr) return [];
        // Split by newlines or commas for flexibility
        return membersStr.split(/[\n\r,↵]+/).filter(member => member.trim().length > 0);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div 
                ref={modalRef}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full overflow-hidden"
            >
                {/* Header */}
                <div className="bg-secondary-dark p-4 flex items-center justify-between">
                    <h2 className="text-xl font-semibold text-white flex items-center">
                        <FaUserFriends className="mr-2" /> 
                        Team Profile
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-white hover:bg-gray-700 rounded-full p-1 transition-colors"
                        aria-label="Close"
                    >
                        <FaTimes size={20} />
                    </button>
                </div>
                
                {/* Content */}
                <div className="p-6">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-12">
                            <FaSpinner className="animate-spin text-secondary text-3xl mb-4" />
                            <p className="text-gray-600 dark:text-gray-400">Loading team information...</p>
                        </div>
                    ) : error ? (
                        <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-4 rounded-md">
                            <div className="flex">
                                <FaInfoCircle className="text-red-500 flex-shrink-0 mt-1" />
                                <div className="ml-3">
                                    <p className="text-red-700 dark:text-red-400">{error}</p>
                                </div>
                            </div>
                        </div>
                    ) : teamInfo ? (
                        <div className="space-y-6">
                            {/* Team Name */}
                            <div className="text-center pb-4 border-b border-gray-200 dark:border-gray-700">
                                <h3 className="text-2xl font-bold text-secondary-dark dark:text-secondary-light mb-2">
                                    {teamInfo.name}
                                </h3>
                                {teamInfo.rank && (
                                    <div className="inline-flex items-center bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 px-3 py-1 rounded-full text-sm">
                                        <FaMedal className="mr-1 text-yellow-600 dark:text-yellow-500" />
                                        Rank #{teamInfo.rank}
                                    </div>
                                )}
                            </div>
                            
                            {/* Team Content */}
                            <div className="grid md:grid-cols-2 gap-6">
                                {/* Team Members */}
                                <div className="bg-gray-50 dark:bg-gray-750 rounded-lg p-4">
                                    <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center">
                                        <FaUserFriends className="mr-2 text-secondary" />
                                        Team Members
                                    </h4>
                                    
                                    {formatMembers(teamInfo.members).length > 0 ? (
                                        <ul className="space-y-2">
                                            {formatMembers(teamInfo.members).map((member, index) => (
                                                <li key={index} className="flex items-center">
                                                    <FaUserCircle className="text-gray-400 mr-2" />
                                                    <span className="text-gray-700 dark:text-gray-300">{member.trim()}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <p className="text-gray-500 dark:text-gray-400 italic">No team members listed</p>
                                    )}
                                </div>
                                
                                {/* Team Description */}
                                <div className="bg-gray-50 dark:bg-gray-750 rounded-lg p-4">
                                    <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center">
                                        <FaInfoCircle className="mr-2 text-secondary" />
                                        About the Team
                                    </h4>
                                    
                                    <div className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap overflow-auto max-h-48">
                                        {teamInfo.description ? (
                                            teamInfo.description
                                        ) : (
                                            <p className="text-gray-500 dark:text-gray-400 italic">No team description provided</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                            
                            {/* Team Statistics (if available) */}
                            {(teamInfo.score || teamInfo.submissions) && (
                                <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                                    <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-3">
                                        Team Statistics
                                    </h4>
                                    
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                        {teamInfo.score !== undefined && (
                                            <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg text-center">
                                                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                                                    {teamInfo.score}
                                                </div>
                                                <div className="text-sm text-blue-600 dark:text-blue-500">
                                                    Best Score
                                                </div>
                                            </div>
                                        )}
                                        
                                        {teamInfo.submissions !== undefined && (
                                            <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg text-center">
                                                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                                                    {teamInfo.submissions}
                                                </div>
                                                <div className="text-sm text-green-600 dark:text-green-500">
                                                    Submissions
                                                </div>
                                            </div>
                                        )}
                                        
                                        {teamInfo.created_at && (
                                            <div className="bg-purple-50 dark:bg-purple-900/20 p-3 rounded-lg text-center">
                                                <div className="text-sm font-medium text-purple-600 dark:text-purple-400">
                                                    {new Date(teamInfo.created_at).toLocaleDateString()}
                                                </div>
                                                <div className="text-sm text-purple-600 dark:text-purple-500">
                                                    Joined
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="text-center py-12">
                            <p className="text-gray-500 dark:text-gray-400">No team information available</p>
                        </div>
                    )}
                </div>
                
                {/* Footer */}
                {!loading && !error && teamInfo && (
                    <div className="bg-gray-50 dark:bg-gray-750 p-4 flex justify-end border-t border-gray-200 dark:border-gray-700">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                        >
                            Close
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default TeamInfoModal;